import React from 'react';
import { motion } from 'framer-motion';
import {
  TrendingDown,
  Clock,
  Award,
  Users,
} from 'lucide-react';

const benefits = [
  {
    icon: <TrendingDown className="h-8 w-8" />,
    title: 'Sin Inversión Inicial',
    description: 'Planes flexibles para cada presupuesto. Comienza sin inversión inicial.',
    stat: '$0',
    color: 'green',
    bgColor: 'bg-green-50',
    textColor: 'text-green-600',
    statColor: 'bg-green-600'
  },
  {
    icon: <Clock className="h-8 w-8" />,
    title: 'Sin Mínimo de Consumo',
    description: 'Planes sin mínimo de consumo. Paga solo por lo que usas.',
    stat: 'Flex',
    color: 'blue',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-600',
    statColor: 'bg-blue-600'
  },
  {
    icon: <Award className="h-8 w-8" />,
    title: 'Equipos Canon y HP',
    description: 'Equipos multifuncionales de última generación de marcas líderes.',
    stat: 'Premium',
    color: 'purple',
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-600',
    statColor: 'bg-purple-600'
  },
  {
    icon: <Users className="h-8 w-8" />,
    title: '+15 Años de Experiencia',
    description: 'Más de 15 años sirviendo empresas en Cabo San Lucas, San José del Cabo y La Paz.',
    stat: '15+',
    color: 'orange',
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-600',
    statColor: 'bg-orange-600'
  },
];

export const Benefits = () => {
  return (
    <section id="beneficios" className="py-20 lg:py-28 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-primary font-semibold text-sm uppercase tracking-wide">
            Beneficios
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mt-3">
            ¿Por Qué Elegirnos?
          </h2>
          <p className="text-lg text-gray-600 mt-4 max-w-2xl mx-auto">
            Experiencia, calidad y compromiso en cada servicio
          </p>
        </motion.div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="relative group"
            >
              <div className="bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 h-full">
                {/* Stat Badge */}
                <div className={`absolute -top-4 -right-4 ${benefit.statColor} text-white px-4 py-2 rounded-full font-bold text-sm shadow-lg`}>
                  {benefit.stat}
                </div>

                <div className={`${benefit.bgColor} w-16 h-16 rounded-xl flex items-center justify-center ${benefit.textColor} mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  {benefit.icon}
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Coverage Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 bg-white rounded-3xl p-8 shadow-lg border border-gray-100"
        >
          <div className="text-center mb-8">
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Nuestra Cobertura en BCS
            </h3>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Actualmente brindamos servicio a empresas y clientes en estas ubicaciones estratégicas
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-red-50 rounded-xl">
              <div className="w-12 h-12 bg-red-600 rounded-lg mx-auto mb-4 flex items-center justify-center">
                <span className="text-white font-bold text-lg">CSL</span>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Cabo San Lucas</h4>
              <p className="text-gray-600 text-sm">Centro turístico y comercial principal</p>
            </div>
            
            <div className="text-center p-6 bg-blue-50 rounded-xl">
              <div className="w-12 h-12 bg-blue-600 rounded-lg mx-auto mb-4 flex items-center justify-center">
                <span className="text-white font-bold text-lg">SJ</span>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">San José del Cabo</h4>
              <p className="text-gray-600 text-sm">Zona hotelera y empresarial</p>
            </div>
            
            <div className="text-center p-6 bg-green-50 rounded-xl">
              <div className="w-12 h-12 bg-green-600 rounded-lg mx-auto mb-4 flex items-center justify-center">
                <span className="text-white font-bold text-lg">LP</span>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">La Paz</h4>
              <p className="text-gray-600 text-sm">Capital del estado, gobierno y empresas</p>
            </div>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16 bg-gradient-to-r from-blue-700 via-indigo-800 to-blue-300 rounded-3xl p-8 sm:p-12 text-center shadow-2xl"
        >
          <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
            ¿Listo para optimizar tu negocio?
          </h3>
          <p className="text-indigo-50 text-lg mb-8 max-w-2xl mx-auto">
            Obtén una cotización personalizada sin compromiso
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              const element = document.getElementById('contacto');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="bg-white text-indigo-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-50 transition-colors duration-200 shadow-lg inline-block"
          >
            Solicitar Cotización Gratuita
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default Benefits;
